import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const RulerIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" />
    <path d="m14.5 12.5 2-2" />
    <path d="m11.5 9.5 2-2" />
    <path d="m8.5 6.5 2-2" />
    <path d="m17.5 15.5 2-2" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CabinetQuotingSoftware = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Structured data for Cabinetry page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product", 
    "name": "Cabinet Quoting Software by QuoteTier",
    "description": "Quoting software for cabinet makers and joiners. Create interactive Good/Better/Best quotes for kitchens, vanities, and custom built-ins.",
    "category": "Software",
    "brand": {
      "@type": "Brand",
      "name": "QuoteTier"
    },
    "offers": {
      "@type": "Offer",
      "name": "Free Trial",
      "price": "0",
      "priceCurrency": "USD"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Cabinet Makers, Joiners, Woodworkers"
    }
  };

  useEffect(() => {
    analytics.track('Cabinetry Landing Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Best Cabinet Quoting Software | Kitchen & Joinery Estimating"
        description="Stop the 'software silo' madness. QuoteTier helps cabinet makers send interactive Good/Better/Best quotes for shaker, flat-panel, and custom joinery."
        canonical="/cabinet-quoting-software"
        structuredData={structuredData}
        ogTitle="Cabinet Quoting Software - Break the Software Silo"
        ogDescription="Most cabinet software is for design, not sales. Use QuoteTier to present tiered options (Melamine vs 2-Pac) that clients actually understand."
        noindex={true}
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
                Cabinet Quoting Software That <span className="text-primary font-bold">Upsells the Finish</span>
              </h1>
              <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
                Break free from the "software silo." Most cabinet software is for CAM, not closing deals. Create interactive Good/Better/Best proposals for your joinery.
              </p>
              <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
                <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'cabinet-hero', action: 'signup' })}>
                  <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                    Start Free — 3 Quotes/Mo
                  </Button>
                </Link>
                <Link to="/demo-quote/cabinetry" onClick={() => analytics.track('Demo Clicked', { location: 'cabinet-hero', industry: 'cabinetry' })}>
                  <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                    See Cabinet Quote Demo →
                  </Button>
                </Link>
              </div>
              <p className="font-jetbrains mt-10 text-lg text-gray-500">
                No credit card required • Setup in 5 minutes • Works on mobile
              </p>
            </div>
          </div>
        </section>

        {/* Cabinet Value Props */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Why Cabinet Makers Need Better Sales Tools</h2>
              <p className="font-jetbrains text-lg text-gray-600">Your CAM software is great for cutting, but terrible for selling.</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Stop the "Software Silo"</CardTitle>
                  <CardDescription className="font-jetbrains">You use expensive software for design (CAD) and manufacturing (CAM), but often just Excel for quoting. Bridge the gap with a dedicated sales tool.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />No more manual Word docs</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Professional presentation every time</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Tiered Finish Options</CardTitle>
                  <CardDescription className="font-jetbrains">Clients always ask "What if we did 2-Pac instead of Melamine?" Show them both prices instantly side-by-side.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Upsell premium finishes effortlessly</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Show visual samples of materials</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Hardware Upgrades</CardTitle>
                  <CardDescription className="font-jetbrains">Soft-close drawers, pull-out pantries, and corner organizers. Let clients "add to cart" the features they want.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Increase profit per kitchen</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Reduce back-and-forth emails</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Stone & Benchtop Allowances</CardTitle>
                  <CardDescription className="font-jetbrains">Set clear allowances for benchtops. If they pick a Category 4 stone instead of Category 1, the upgrade price is clear.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Avoid margin erosion</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Clear scope expectations</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                QuoteTier vs Traditional Methods
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                Move from "Estimate" to "Experience"
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Feature
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-instrument text-lg font-bold">QuoteTier</span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      Excel / Word
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Interactive "Pick Your Finish"</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Automatic Hardware Upsells</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Mobile-Friendly for Site Visits</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">View Tracking (Know when they look)</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-gray-900 to-black text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Ready to Modernize Your Joinery Business?
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-8">
              Join cabinet makers who are winning more high-end kitchens with interactive quotes.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'cabinet-final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/cabinetry" onClick={() => analytics.track('Demo Clicked', { location: 'cabinet-final-cta', industry: 'cabinetry' })}>
                <Button className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white/10 rounded-lg" variant="outline">
                  Try Interactive Demo First →
                </Button>
              </Link>
            </div>
            
            <p className="font-jetbrains text-sm text-gray-300">
              No credit card required • Cancel anytime • Setup in minutes
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t">
        <div className="container max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">&copy; 2025 QuoteTier. All rights reserved. <span className="footer-credit">Site by <a href="https://novanorthstudio.com/?utm_source=quotetier.com&utm_medium=referral&utm_campaign=site-credit">Nova North Studio</a></span></p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <a href="mailto:info@quotetier.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CabinetQuotingSoftware;
